import { useState, useEffect, Fragment } from 'react';
import './App.scss';
import LandingPage from './Route/Landing';
import Termsofuse from './Route/Termsofuse';
import Privacypolicy from './Route/Privacypolicy';
import Login from './Route/Login';
import KoreanSessionProduct from './Route/Product/KoreanSession';
import Topik250622Product from './Route/Product/Topik250622';
import History from './Route/Purchase/History';
import Payment from './Route/Payment';
import Success from './Route/Payment/Success';
import Failed from './Route/Payment/Failed';
import PaymentLink from './Route/Payment/Link';
import { PrepSurvey, SurveySubmitted } from './Route/KoreanSession';
import TopikDashboard from './Route/Topik';
import TopikTest from './Route/Topik/[questionnaireid]/[attemptid]/[section]';
import TopikResult from './Route/Topik/[questionnaireid]/[attemptid]/[section]/result';
import TopikRolesPage from './Route/Topik/roles';
import TopikExamListPage from './Route/Topik/exam';
import TopikExamDetailPage from './Route/Topik/exam/[examid]';
import TopikExamSectionPage from './Route/Topik/exam/[examid]/[section]';
import GNB from './Components/GNB/GNB';
import AppBar from './Components/AppBar';
import Social from './Route/social';
import ImageViewer from './Route/social/image-viewer';
import PostDetail from './Route/social/post/[postid]';
import CreatePost from './Route/social/post';
import UserProfile from './Route/social/user/[addressid]';
import ChatPage from './Route/chat';
import BotProfile from './Route/bot/[botid]';
import { RecoilRoot } from 'recoil';
import Topik250725Product from './Route/Product/Topik250725';
import GksSeminarProduct from './Route/Product/GksSeminar';
import StudyAbroadMateProduct from './Route/Product/StudyAbroadMate';
import AdventureKorea250912 from './Route/Notice/AdventureKorea250912';
import UnlimitedChat251006 from './Route/Notice/UnlimitedChat251006';
import University from './Route/Admission/University';
import UniversityProgress from './Route/Admission/University/Progress';
import { useSession } from './Recoil/Session/Session';
import Admission from './Route/Admission';
import Curriculum251202 from './Route/Notice/Curriculum251202';
import KoreanStudyTypeRouter from './Route/Personatest/koreanstudytype';
import { logBehavior } from './Data/Behavior';
import { stripLanguagePrefix } from './Utils/routing';
import { languageManager } from './Utils/languageManager';
import AboutCurriculum from './Route/About/Curriculum';
import DialogAlert from './Components/DialogAlert';
import AdmissionQnA from './Route/Admission/QnA';

function AppWrapper() {

    return <RecoilRoot>
        <DialogAlert.Context>
            <App />
        </DialogAlert.Context>
    </RecoilRoot>
};

function App() {
    const [currentPath, setCurrentPath] = useState(stripLanguagePrefix(window.location.pathname));
    const [, setCurrentSearch] = useState(window.location.search);

    const { sessionRefresh } = useSession();

    useEffect(() => {
        sessionRefresh();
        // Sync language from URL on initial load
        languageManager.syncFromUrl();
        // Log page view - fire and forget
        logBehavior('pageView', {
            page: window.location.pathname,
            type: 'initial'
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            // Sync language from URL on navigation
            languageManager.syncFromUrl();
            // Strip language prefix for route matching
            setCurrentPath(stripLanguagePrefix(window.location.pathname));
            setCurrentSearch(window.location.search);
            // Log page view on navigation - fire and forget
            logBehavior('pageView', {
                page: window.location.pathname,
                type: 'navigation'
            });
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Routes where GNB should be hidden
    const hideGNBRoutes = ['/', '/login', '/termsofuse', '/privacypolicy',];
    const hideGNBPrefixes = ['/bot/', '/notice', '/personatest', '/admission/qna'];
    const shouldShowGNB = !hideGNBRoutes.includes(currentPath) && !hideGNBPrefixes.some(prefix => currentPath.startsWith(prefix));
    
    // Routes that should show AppBar (mobile-style navigation)
    const showAppBarRoutes = ['/social', '/chat', '/topik', '/profile'];
    const shouldShowAppBar = showAppBarRoutes.some(route => currentPath.startsWith(route));

    // Determine active tab for AppBar
    const getActiveTab = () => {
        if (currentPath.startsWith('/social')) return 'social';
        if (currentPath.startsWith('/chat')) return 'mate';
        if (currentPath.startsWith('/topik')) return 'topik';
        if (currentPath.startsWith('/profile')) return 'profile';
        if (currentPath === '/') return 'home';
        return undefined;
    };

    const content = () => {
        const path = currentPath;
        const Notfound = () => <div className={"NotFound"}>{"Not Found!"}</div>;
        
        // Static routes
        if (path === '/') {
            return <LandingPage/>;
        } else if (path === '/termsofuse') {
            return <Termsofuse />;
        } else if (path === '/privacypolicy') {
            return <Privacypolicy/>;
        } else if (path === '/login') {
            return <Login />;
        } else if (path.startsWith('/about')) {
            if (path === '/about/curriculum') {
                return <AboutCurriculum/>;
            }else {
                return <Notfound />;
            }
        } else if (path === '/payment/link') {
            return <PaymentLink />;
        } else if (path === '/payment/success') {
            return <Success />;
        } else if (path === '/payment/failed') {
            return <Failed />;
        } else if (path === '/payment') {
            // Get client secret from URL query param
            const urlParams = new URLSearchParams(window.location.search);
            const clientSecret = urlParams.get('client_secret');
            return <Payment clientSecret={clientSecret} />;
        } else if (path === '/purchase/history') {
            return <History />;
        } else if (path === '/korean-session/prep-survey') {
            return <PrepSurvey onSubmit={(formData) => {
                console.log('Survey submitted:', formData);
                // Handle form submission, such as sending to backend
            }} />;
        } else if (path === '/korean-session/survey-submitted') {
            return <SurveySubmitted />;
        } else if (path === '/topik') {
            return <TopikDashboard />;
        } else if (path === '/topik/roles') {
            return <TopikRolesPage />;
        } else if (path === '/topik/exam') {
            return <TopikExamListPage />;
        } else if (path.match(/^\/topik\/exam\/([^/]+)\/(listening|reading|writing)$/)) {
            const examid = path.split('/')[3];
            const section = path.split('/')[4] as 'listening' | 'reading' | 'writing';
            return <TopikExamSectionPage examid={examid} section={section} />;
        } else if (path.match(/^\/topik\/exam\/([^/]+)$/)) {
            const examid = path.split('/')[3];
            return <TopikExamDetailPage examid={examid} />;
        } else if (path.match(/^\/topik\/[^/]+\/[^/]+\/(listening|reading|writing)\/result$/)) {
            const pathParts = path.split('/');
            const questionnaireid = pathParts[2];
            const attemptid = pathParts[3];
            const section = pathParts[4] as 'listening' | 'reading' | 'writing';
            return <TopikResult questionnaireid={questionnaireid} attemptid={attemptid} section={section} />;
        } else if (path.match(/^\/topik\/[^/]+\/[^/]+\/(listening|reading|writing)$/)) {
            const pathParts = path.split('/');
            const questionnaireid = pathParts[2];
            const attemptid = pathParts[3];
            const section = pathParts[4] as 'listening' | 'reading' | 'writing';
            return <TopikTest questionnaireid={questionnaireid} attemptid={attemptid} section={section} />;
        } else if (path === '/social') {
            return <Social />;
        } else if (path === '/social/post') {
            return <CreatePost />;
        } else if (path.startsWith('/social/post/')) {
            // Match /social/post/:postid
            const postMatch = path.match(/^\/social\/post\/([^/]+)$/);
            if (postMatch) {
                return <PostDetail />;
            } else {
            return <Notfound />;
            }
        } else if (path.startsWith('/social/image/')) {
            // Match /social/image/:postId/:imageIndex
            const imageMatch = path.match(/^\/social\/image\/([^/]+)\/(\d+)$/);
            if (imageMatch) {
                return <ImageViewer />;
            } else {
            return <Notfound />;
            }
        } else if (path.startsWith('/social/user/')) {
            // Match /social/user/:addressid
            const userMatch = path.match(/^\/social\/user\/([^/]+)$/);
            if (userMatch) {
                return <UserProfile />;
            } else {
            return <Notfound />;
            }
        } else if (path === '/product/korean-session') {
            return <KoreanSessionProduct productId="korean-session" />;
        } else if (path === '/product/topik-250622') {
            return <Topik250622Product />;
        } else if (path === '/product/topik-250725') {
            return <Topik250725Product />;
        } else if (path === '/product/gks-seminar') {
            return <GksSeminarProduct />;
        } else if (path === '/product/study-abroad-mate') {
            return <StudyAbroadMateProduct />;
        } else if (path === '/notice/250912-adventure-korea') {
            return <AdventureKorea250912 />;
        } else if (path === '/notice/251006-unlimitedchat') {
            return <UnlimitedChat251006 />;
        } else if (path === '/notice/251202-curriculum') {
            return <Curriculum251202 />;
        } else if (path === '/admission/qna') {
            return <AdmissionQnA />;
        } else if (path.match(/^\/admission\/qna\/([^/]+)$/)) {
            const sessionid = path.split('/')[3];
            return <AdmissionQnA sessionid={sessionid} />;
        } else if (path.match(/^\/admission\/([^/]+)\/([^/]+)$/)) {
            const universityId = path.split('/')[2];
            const trackId = path.split('/')[3];
            return <UniversityProgress universityId={universityId} trackId={trackId} />;
        } else if (path.match(/^\/admission\/([^/]+)$/)) {
            const universityId = path.split('/')[2];
            return <University universityId={universityId} />;
        } else if (path === '/admission') {
            return <Admission />;
        } else if (path.startsWith('/personatest/')) {
            let remainder = path.replace('/personatest', '');
            if (remainder.startsWith('/koreanstudytype')) {
                const subpath = remainder.replace('/koreanstudytype', '') || '/intro';
                return <KoreanStudyTypeRouter subpath={subpath} />;
            } else {
                return <KoreanStudyTypeRouter subpath="/intro" />;
            }
        } else if (path === '/chat') {
            return <ChatPage />;
        } else if (path.startsWith('/bot/')) {
            const botMatch = path.match(/^\/bot\/([^/]+)$/);
            if (botMatch) {
                return <BotProfile />;
            } else {
            return <Notfound />;
            }
        } else {
            return <Notfound />;
        }

        // never reach here
        // eslint-disable-next-line
        throw new Error('Unhandled route');
    };

    return (<Fragment>
            {shouldShowGNB && <GNB />}
            {content()}
            {shouldShowAppBar && <AppBar activeTab={getActiveTab()} />}
    </Fragment>);
}

export default AppWrapper;